import { useSuspenseQuery } from '@apollo/client/react';
import type { UseSuspenseQueryResult } from '@apollo/client/react/hooks/useSuspenseQuery';

import { LIMIT_FOR_FETCHING_ITEMS } from '~/shared/constants';
import { companyFragment } from '~/shared/graphql-queries/companies';
import { itemsPagination, readQuery } from '~/shared/helpers/apollo-helpers';
import type { PaginationVariablesType } from '~/shared/types/general-type';

type PaginationCompanyQueryType = {
  paginationCompanyQuery: {
    items: [];
    pageInfo: {
      hasPreviousPage: false;
      hasNextPage: false;
      currentPage: 1;
      perPage: 1;
      itemCount: 0;
      pageCount: 1;
    };
  };
};

type CompanyFilterOptionsType = {
  filter?: Record<string, unknown> | null;
  nextPage?: number;
  perPage?: number;
  sort?: string;
};

type FetchMoreCompanyParamsType = {
  nextPage?: number;
  sort?: string;
  perPage?: number;
};

export const usePaginationCompanyQuery = (
  filterOptions: CompanyFilterOptionsType = {},
  options: Record<string, unknown> = {},
) => {
  const useQueryProps: UseSuspenseQueryResult<PaginationCompanyQueryType, PaginationVariablesType> = useSuspenseQuery(
    itemsPagination({
      entity: 'company',
      fragment: companyFragment,
      filterType: 'FilterFindManyCompanyInput',
    }),
    {
      variables: {
        filter: filterOptions.filter ?? null,
        nextPage: filterOptions.nextPage ?? 1,
        sort: filterOptions.sort ?? '_ID_DESC',
        perPage: filterOptions.perPage ?? LIMIT_FOR_FETCHING_ITEMS,
      },
      ...options,
    },
  );

  const { refetch, error, data, fetchMore } = useQueryProps;

  const fetchMoreHandler = async ({ nextPage, sort, perPage }: FetchMoreCompanyParamsType) => {
    await fetchMore(
      readQuery<PaginationCompanyQueryType, PaginationVariablesType>({
        query: 'paginationCompanyQuery',
        filter: filterOptions.filter ?? null,
        nextPage: nextPage ?? 1,
        sort: sort ?? filterOptions.sort ?? '_ID_DESC',
        perPage: perPage ?? filterOptions.perPage ?? LIMIT_FOR_FETCHING_ITEMS,
        isLazyLoading: false,
      }),
    );
  };

  const refetchHandler = async (variables?: Partial<PaginationVariablesType>) => {
    await refetch(
      variables
        ? {
            filter: variables.filter ?? filterOptions.filter ?? null,
            nextPage: variables.nextPage ?? 1,
            sort: variables.sort ?? filterOptions.sort ?? '_ID_DESC',
            perPage: variables.perPage ?? filterOptions.perPage ?? LIMIT_FOR_FETCHING_ITEMS,
          }
        : undefined,
    );
  };

  return {
    error,
    refetch: refetchHandler,
    data: data ?? {
      paginationCompanyQuery: {
        items: [],
        pageInfo: {
          hasPreviousPage: false,
          hasNextPage: false,
          currentPage: 1,
          perPage: 1,
          itemCount: 0,
          pageCount: 1,
        },
      },
    },
    fetchMore: fetchMoreHandler,
  };
};
